import { Box, Stack, Tab, Tabs, Tooltip, Typography, useTheme } from '@mui/material'
import React from 'react'
import { FormFieldType } from '../../../enums'
import DraggableField from './DragableField'
import {
  CalendarMonth,
  CheckBox,
  DateRangeTwoTone,
  FileUpload,
  FormatAlignCenter,
  List,
  Note,
  Numbers,
  RadioButtonChecked,
  SelectAll,
  TextFields,
  Timer,
  Article,
  Image,
  AttachFile,
  FactCheck,
} from '@mui/icons-material'
import FormValidation from './FormValidation'
import { useTranslation } from 'react-i18next'
import { useDialogCtx } from 'src/hooks/context-hooks'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { SchemaType } from 'src/generated-types/form-schema'

const TabPanel = ({ children, value, index, classes = [] }) => {
  return value == index ? <Stack spacing={1}>{children}</Stack> : <></>
}

const FormSettings = ({
  onDrag,
  openTab,
  setOpenTab,
  setLayouts,
  selectedSetting,
  updateField,
  setSelectedSettingField,
  onDelete,
  selectedRowIndex,
  selectedColumnIndex,
  selectedFieldIndex,
  schemaType,
}) => {
  const { t } = useTranslation(['form-schema', 'common'])
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setOpenTab(newValue)
  }
  const theme = useTheme()
  const { showConfirmDialog } = useDialogCtx()

  return (
    <Box width={200}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={openTab} onChange={handleChange} aria-label="basic tabs example">
          <Tab
            label={
              <Typography
                color={
                  openTab == 0
                    ? `${theme.palette.primaryMain[theme.palette.mode]} !important`
                    : `${theme.palette.secondaryMain[theme.palette.mode]} !important`
                }
              >
                {t('fields')}
              </Typography>
            }
            value={0}
          />
          <Tab
            label={
              <Typography
                color={
                  openTab == 1
                    ? `${theme.palette.primaryMain[theme.palette.mode]} !important`
                    : `${theme.palette.secondaryMain[theme.palette.mode]} !important`
                }
              >
                {t('settings')}
              </Typography>
            }
            value={1}
          />
        </Tabs>
      </Box>
      <Box height={window.innerHeight - 285} className="scrollbar" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
        <TabPanel value={openTab} index={0}>
          {schemaType == SchemaType.Checklist && (
            <Box pb={4}>
              <Box pt={2} pb={1} display={'flex'} alignItems={'center'} gap={0.5}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('checklist')}
                </Typography>
              </Box>
              <DraggableField icon={<FactCheck />} name="Checklist" value={FormFieldType.checklist} onDrag={onDrag} />
            </Box>
          )}
          <Box pb={4}>
            <Box pt={2} pb={1} display={'flex'} alignItems={'center'} gap={0.5}>
              <Typography variant="subtitle2" color="textSecondary">
                {t('instruction-fields')}
              </Typography>
              <Tooltip title={`${t('instruction-fields-info')}`}>
                <InfoOutlinedIcon color="action" style={{ fontSize: 18, cursor: 'pointer' }} />
              </Tooltip>
            </Box>
            <DraggableField icon={<Article />} name="Rich Text" value={FormFieldType.richText} onDrag={onDrag} />
            <DraggableField icon={<Image />} name="Image" value={FormFieldType.instructionImage} onDrag={onDrag} />
            <DraggableField icon={<AttachFile />} name="File" value={FormFieldType.instructionFile} onDrag={onDrag} />
          </Box>
          <DraggableField icon={<FormatAlignCenter />} name="Title" value={FormFieldType.title} onDrag={onDrag} />
          <DraggableField icon={<TextFields />} name="Text Field" value={FormFieldType.textField} onDrag={onDrag} />
          <DraggableField icon={<Numbers />} name="Number" value={FormFieldType.numberField} onDrag={onDrag} />
          <DraggableField icon={<CheckBox />} name="CheckBox" value={FormFieldType.checkBox} onDrag={onDrag} />
          <DraggableField
            icon={<CalendarMonth />}
            name="Date Picker"
            value={FormFieldType.datePicker}
            onDrag={onDrag}
          />
          <DraggableField icon={<Note />} name="Text Area" value={FormFieldType.textArea} onDrag={onDrag} />
          <DraggableField icon={<DateRangeTwoTone />} name="Date Time" value={FormFieldType.dateTime} onDrag={onDrag} />
          <DraggableField icon={<FileUpload />} name="File Upload" value={FormFieldType.fileUpload} onDrag={onDrag} />
          <DraggableField icon={<Timer />} name="Time Picker" value={FormFieldType.timePicker} onDrag={onDrag} />
          <DraggableField icon={<RadioButtonChecked />} name="Radio" value={FormFieldType.radio} onDrag={onDrag} />
          <DraggableField icon={<List />} name="Checkbox List" value={FormFieldType.checkBoxList} onDrag={onDrag} />
          <DraggableField icon={<SelectAll />} name="Select Field" value={FormFieldType.selectField} onDrag={onDrag} />
        </TabPanel>
        <TabPanel value={openTab} index={1}>
          <FormValidation
            selectedSetting={selectedSetting}
            changeSetting={fieldValues => {
              setSelectedSettingField(fieldValues)
              updateField(fieldValues, selectedRowIndex, selectedColumnIndex, selectedFieldIndex)
            }}
            onDelete={() => {
              showConfirmDialog(
                t('delete-field'),
                t('delete-field-content'),
                {
                  acceptText: t('yes', { ns: 'common' }),
                  cancelText: t('no', { ns: 'common' }),
                },
                async pop => {
                  onDelete()
                  pop()
                },
              )
            }}
          />
        </TabPanel>
      </Box>
    </Box>
  )
}

export default FormSettings
